import { useId, useMemo, useState } from "react";
import { ImagePlus, UploadCloud, X } from "lucide-react";
import { Button } from "@ndd/ui";
import { cn } from "@/lib/utils";
import {
  OBSERVATION_IMAGE_CONTENT_TYPES,
  OBSERVATION_IMAGE_MAX_COUNT,
  type ObservationImageData,
} from "../../../../shared/observation-types";
import { formatObservationImageByteSize } from "@/lib/observation-image-client";
import { ObservationImageCarouselDialog } from "./observation-image-carousel-dialog";

export interface PendingObservationImage {
  id: string;
  file: File;
  previewUrl: string;
}

interface ObservationImagePickerProps {
  files: PendingObservationImage[];
  existingCount: number;
  disabled?: boolean;
  error?: string | null;
  onAddFiles: (files: File[]) => void;
  onRemoveFile: (id: string) => void;
}

const ACCEPTED_IMAGE_TYPES = OBSERVATION_IMAGE_CONTENT_TYPES.join(",");

export function ObservationImagePicker({
  files,
  existingCount,
  disabled = false,
  error,
  onAddFiles,
  onRemoveFile,
}: ObservationImagePickerProps) {
  const inputId = useId();
  const [isDragging, setIsDragging] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const totalCount = existingCount + files.length;
  const isFull = totalCount >= OBSERVATION_IMAGE_MAX_COUNT;
  const pickerDisabled = disabled || isFull;

  function handleFiles(fileList: FileList | null) {
    if (!fileList || pickerDisabled) return;
    onAddFiles(Array.from(fileList));
  }

  const previewImages = useMemo<ObservationImageData[]>(
    () =>
      files.map((file) => ({
        id: file.id,
        contentType: file.file.type,
        byteSize: file.file.size,
        width: null,
        height: null,
        createdAt: "",
        url: file.previewUrl,
      })),
    [files],
  );
  const clampedSelectedIndex = selectedIndex === null || previewImages.length === 0
    ? null
    : Math.min(selectedIndex, previewImages.length - 1);

  return (
    <div className="flex flex-col gap-2">
      <div
        onDragEnter={(event) => {
          event.preventDefault();
          if (!pickerDisabled) setIsDragging(true);
        }}
        onDragOver={(event) => {
          event.preventDefault();
          if (!pickerDisabled) event.dataTransfer.dropEffect = "copy";
        }}
        onDragLeave={(event) => {
          event.preventDefault();
          setIsDragging(false);
        }}
        onDrop={(event) => {
          event.preventDefault();
          setIsDragging(false);
          handleFiles(event.dataTransfer.files);
        }}
        className={cn(
          "rounded-xl border border-dashed border-sand-300 bg-sand-50 px-4 py-4 transition-colors",
          isDragging && "border-interactive bg-interactive-light",
          pickerDisabled && "opacity-60",
        )}
      >
        <input
          id={inputId}
          type="file"
          accept={ACCEPTED_IMAGE_TYPES}
          multiple
          disabled={pickerDisabled}
          className="sr-only"
          onChange={(event) => {
            handleFiles(event.currentTarget.files);
            event.currentTarget.value = "";
          }}
        />
        <div className="flex flex-col items-center gap-2 text-center sm:flex-row sm:text-left">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-card text-interactive shadow-sm">
            <UploadCloud className="h-5 w-5" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[13px] font-semibold text-foreground">
              Drop images here or choose files
            </div>
            <div className="mt-0.5 text-[12px] text-sand-500">
              JPG, PNG, or WebP up to 5MB each. {totalCount} of{" "}
              {OBSERVATION_IMAGE_MAX_COUNT} attached.
            </div>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={pickerDisabled}
            className="h-8 rounded-[10px] border-sand-200 px-3 text-xs font-medium"
            asChild
          >
            <label htmlFor={inputId} className="cursor-pointer">
              <ImagePlus className="h-3.5 w-3.5" />
              Choose images
            </label>
          </Button>
        </div>
      </div>

      {error && <p className="text-[12px] text-error">{error}</p>}

      {files.length > 0 && (
        <div className="flex flex-wrap gap-4">
          {files.map((file, index) => (
            <div
              key={file.id}
              className="relative w-[88px] rounded-[10px] border border-sand-200 bg-card"
            >
              <button
                type="button"
                onClick={() => setSelectedIndex(index)}
                className="block h-[88px] w-[88px] overflow-hidden rounded-[10px] text-left"
                aria-label={`View pending attachment ${index + 1} of ${files.length}`}
              >
                <img
                  src={file.previewUrl}
                  alt={`Pending attachment ${index + 1} of ${files.length}`}
                  className="h-[88px] w-[88px] object-cover object-center"
                />
              </button>
              <button
                type="button"
                onClick={() => onRemoveFile(file.id)}
                disabled={disabled}
                className="absolute -right-2 -top-2 z-20 flex h-7 w-7 items-center justify-center rounded-full bg-bark text-white shadow-sm ring-2 ring-white transition-colors hover:bg-error disabled:opacity-50"
                aria-label={`Remove pending attachment ${index + 1}`}
              >
                <X className="h-4 w-4" />
              </button>
              <div className="px-2 py-1.5 text-[11px] text-sand-500">
                Image {index + 1} · {formatObservationImageByteSize(file.file.size)}
              </div>
            </div>
          ))}
        </div>
      )}

      <ObservationImageCarouselDialog
        key={clampedSelectedIndex ?? "closed"}
        images={previewImages}
        initialIndex={clampedSelectedIndex ?? 0}
        open={clampedSelectedIndex !== null}
        onOpenChange={(open) => {
          if (!open) setSelectedIndex(null);
        }}
      />
    </div>
  );
}
